import { ccRequest } from './CCPublicationService';
import { finite, validId } from '../components/crop-intelligence/publicationContract';
import { isPolygonGeometry } from '../components/crop-intelligence/contract';
import { normalizeExclusions, validateExclusions } from '../components/crop-intelligence/cropExclusions';

export async function createMapScope(snapshot, scope, classCode, access, signal, excludedClassKeys = []) {
  const data = await ccRequest('map-scopes', { method: 'post', signal, data: {
    snapshot_id: snapshot.snapshot_id, kind: scope.kind, subject_id: scope.subject_id,
    membership_revision: scope.membership_revision, class_code: classCode,
    ...(excludedClassKeys.length ? { excluded_class_keys: normalizeExclusions(excludedClassKeys) } : {}),
  } });
  validateExclusions(data, excludedClassKeys);
  if (!validId(data?.map_scope_id) || data.snapshot_id !== snapshot.snapshot_id || data.style_revision !== snapshot.style_revision ||
      data.boundary_revision_id !== snapshot.boundary_revision_id || data.access_revision !== access || data.class_code !== classCode ||
      (scope.membership_revision != null && String(data.membership_revision) !== String(scope.membership_revision)) ||
      !Number.isFinite(Date.parse(data.expires_at))) throw new Error('Invalid CC map scope');
  return { ...data, excludedCropCodes: snapshot.classes.filter((entry) => excludedClassKeys.includes(entry.semantic_key)).map((entry) => entry.code) };
}
export async function viewport(scope, bbox, signal, { boundaries = false, level, budget = 20000 } = {}) {
  const features = new Map(); const cursors = new Set(); let cursor; let pages = 0; let incomplete = false; let clipped = false;
  do {
    if (signal.aborted) throw new Error('Cancelled');
    const data = await ccRequest(`map-scopes/${encodeURIComponent(scope.map_scope_id)}/${boundaries ? 'boundaries' : 'features'}`, {
      signal, params: { bbox: bbox.join(','), cursor, limit: Math.min(200, budget - features.size), ...(boundaries ? { level } : {}) },
    });
    if (data?.snapshot_id !== scope.snapshot_id || data.map_scope_id !== scope.map_scope_id || !Array.isArray(data.features) || data.features.length > 200 || typeof data.incomplete !== 'boolean') throw new Error('Invalid CC viewport identity');
    validateExclusions(data, scope.excluded_class_keys);
    clipped = clipped || data.geometry_clipped_to_bbox !== false;
    for (const feature of data.features) {
      const p = feature.properties;
      if (!validId(p?.fid) || p.snapshot_id !== scope.snapshot_id || p.map_scope_id !== scope.map_scope_id || !isPolygonGeometry(feature.geometry)) throw new Error('Invalid CC feature');
      if (!boundaries && (!Number.isInteger(p.crop_code) || !finite(p.area_ha) || p.area_ha < 0 || scope.excludedCropCodes?.includes(p.crop_code) || (scope.class_code != null && p.crop_code !== scope.class_code))) throw new Error('Invalid CC crop feature');
      if (features.has(p.fid)) incomplete = true;
      features.set(p.fid, { ...feature, geometry_clipped_to_bbox: data.geometry_clipped_to_bbox !== false });
    }
    cursor = data.next_cursor;
    pages += 1;
    if (data.incomplete && !cursor) incomplete = true;
    if (cursor && (cursors.has(cursor) || features.size >= budget || pages >= 100)) return { features: [...features.values()], incomplete: true, clipped };
    cursors.add(cursor);
  } while (cursor);
  return { features: [...features.values()], incomplete, clipped };
}

export function requestQueue(concurrency = 2, maxPending = 256) {
  let running = 0; let closed = false; const pending = [];
  const finished = () => { running -= 1; pump(); };
  const pump = () => {
    while (!closed && running < concurrency && pending.length) {
      const task = pending.shift();
      task.signal.removeEventListener('abort', task.cancel);
      if (task.signal.aborted) { task.reject(new Error('Cancelled')); continue; }
      running += 1;
      Promise.resolve().then(task.run).then(task.resolve, task.reject).finally(finished);
    }
  };
  return {
    run: (run, signal) => new Promise((resolve, reject) => {
      if (closed || signal.aborted) { reject(new Error('Cancelled')); return; }
      if (pending.length >= maxPending) { reject(new Error('CC tile queue full')); return; }
      const task = { run, signal, resolve, reject };
      task.cancel = () => {
        const index = pending.indexOf(task);
        if (index !== -1) pending.splice(index, 1);
        signal.removeEventListener('abort', task.cancel);
        reject(new Error('Cancelled'));
      };
      signal.addEventListener('abort', task.cancel, { once: true });
      pending.push(task); pump();
    }),
    close: () => { closed = true; [...pending].forEach((task) => task.cancel()); },
  };
}
